import { dimensionBadgeBase } from '@src/components/common/dimensionBadgeStyles';
import Modal from '@src/components/common/Modal';
import Switch from '@src/components/common/Switch';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import CloseIcon from '@src/svgs/CloseIcon';
import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import type { VideoTemplateElementType } from './VideoTemplatesContext';

export type ElementOption = {
  type: VideoTemplateElementType;
  label: string;
  /** Whether this element exists in the template config */
  exists: boolean;
  /** Current enabled state from the DatoCMS config */
  configEnabled: boolean;
  /** Current "lock position" state from the DatoCMS config */
  configLockPosition: boolean;
  /** Whether this element supports position updates (logo, captions, nameCard) */
  hasPlacement: boolean;
  /** Whether the element is active in the editor (needed to update position) */
  activeInEditor: boolean;
  /** Whether a local replacement exists (different composition/logo/captions than template default) */
  hasReplacement: boolean;
  /** Whether the live editor placement differs from the template's stored placement */
  placementDiffersFromTemplate: boolean;
};

export type UpdateTemplatePlacementResult = {
  enabledChanges: Partial<Record<VideoTemplateElementType, boolean>>;
  placementTypes: VideoTemplateElementType[];
  replacementTypes: VideoTemplateElementType[];
  lockPositionChanges: Partial<Record<VideoTemplateElementType, boolean>>;
};

interface UpdateTemplatePlacementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (result: UpdateTemplatePlacementResult) => Promise<void>;
  onSaveAsCopy: (
    result: UpdateTemplatePlacementResult,
    label: string,
  ) => Promise<void>;
  options: ElementOption[];
  /**
   * Whether the current user may adjust the per-element "Lock Position" toggles
   * (feature flag + admin privilege). When false the lock rows are hidden.
   */
  canEditLockPosition?: boolean;
  /** Name of the template the changes will be written to. */
  templateName?: string;
  /**
   * Aspect ratio whose configuration receives the changes (e.g. "16:9").
   * Templates store one configuration per aspect ratio, so the modal names the
   * version being updated rather than letting it read as "the whole template".
   */
  aspectRatio?: string;
}

const TOOLTIP_ID = 'update-template-placement-modal-tooltip';

export function UpdateTemplatePlacementModal({
  isOpen,
  onClose,
  onUpdate,
  onSaveAsCopy,
  options,
  canEditLockPosition = false,
  templateName,
  aspectRatio,
}: UpdateTemplatePlacementModalProps) {
  const [enabledStates, setEnabledStates] = useState<Record<string, boolean>>(
    {},
  );
  const [placementSelections, setPlacementSelections] = useState<
    Record<string, boolean>
  >({});
  const [replacementSelections, setReplacementSelections] = useState<
    Record<string, boolean>
  >({});
  const [lockSelections, setLockSelections] = useState<Record<string, boolean>>(
    {},
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSavingCopy, setIsSavingCopy] = useState(false);
  const [copyLabel, setCopyLabel] = useState('');
  const [showCopyInput, setShowCopyInput] = useState(false);

  // Re-sync state from options every time the modal opens
  // Do not re-sync on options change
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!isOpen) return;
    const opts = optionsRef.current;

    const newEnabledStates = Object.fromEntries(
      opts.map(o => [o.type, o.activeInEditor]),
    );
    // Default placement ON only when the live position actually differs from
    // the template — otherwise applying it is a no-op and shouldn't pre-light
    // the Update button.
    const newPlacementSelections = Object.fromEntries(
      opts.map(o => [
        o.type,
        o.hasPlacement && o.activeInEditor && o.placementDiffersFromTemplate,
      ]),
    );
    // Default replacement ON when a local replacement exists
    const newReplacementSelections = Object.fromEntries(
      opts.map(o => [o.type, o.hasReplacement]),
    );
    // Seed lock toggles from the stored config value
    const newLockSelections = Object.fromEntries(
      opts.map(o => [o.type, o.configLockPosition]),
    );

    setEnabledStates(newEnabledStates);
    setPlacementSelections(newPlacementSelections);
    setReplacementSelections(newReplacementSelections);
    setLockSelections(newLockSelections);
    setCopyLabel('');
    setShowCopyInput(false);
  }, [isOpen]);

  const handleToggleEnabled = (type: string) => {
    setEnabledStates(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const handleTogglePlacement = (type: string) => {
    setPlacementSelections(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const handleToggleReplacement = (type: string) => {
    setReplacementSelections(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const handleToggleLock = (type: string) => {
    setLockSelections(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const buildResult = (): UpdateTemplatePlacementResult => {
    // Send all enabled states that differ from the DatoCMS config
    const enabledChanges: Partial<Record<VideoTemplateElementType, boolean>> =
      {};
    for (const option of options) {
      if (
        option.exists &&
        enabledStates[option.type] !== option.configEnabled
      ) {
        enabledChanges[option.type] = enabledStates[option.type];
      }
    }

    const placementTypes = options
      .filter(
        o => o.hasPlacement && o.activeInEditor && placementSelections[o.type],
      )
      .map(o => o.type);

    const replacementTypes = options
      .filter(o => o.hasReplacement && replacementSelections[o.type])
      .map(o => o.type);

    // Only emit lock changes when the user may edit them and the value differs
    // from what the config stored.
    const lockPositionChanges: Partial<
      Record<VideoTemplateElementType, boolean>
    > = {};
    if (canEditLockPosition) {
      for (const option of options) {
        if (
          option.exists &&
          lockSelections[option.type] !== option.configLockPosition
        ) {
          lockPositionChanges[option.type] = lockSelections[option.type];
        }
      }
    }

    return {
      enabledChanges,
      placementTypes,
      replacementTypes,
      lockPositionChanges,
    };
  };

  const hasChanges = (): boolean => {
    const result = buildResult();
    return (
      Object.keys(result.enabledChanges).length > 0 ||
      result.placementTypes.length > 0 ||
      result.replacementTypes.length > 0 ||
      Object.keys(result.lockPositionChanges).length > 0
    );
  };

  const handleUpdate = async () => {
    if (!hasChanges() || !onUpdate) return;

    setIsUpdating(true);
    try {
      await onUpdate(buildResult());
      onClose();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveAsCopy = async () => {
    if (!copyLabel.trim()) return;

    setIsSavingCopy(true);
    try {
      await onSaveAsCopy(buildResult(), copyLabel.trim());
      onClose();
    } finally {
      setIsSavingCopy(false);
    }
  };

  const handleCopyKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && copyLabel.trim()) {
      handleSaveAsCopy();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      closeButtonStyle={{ display: 'none' }}
    >
      <ModalWrapper>
        <Header>
          <Title>Update Video Template</Title>
          <CloseButton onClick={onClose}>
            <CloseIcon width="12" height="12" strokeColor="currentColor" />
          </CloseButton>
        </Header>
        {(templateName || aspectRatio) && (
          <TargetRow>
            {templateName && <TemplateName>{templateName}</TemplateName>}
            {aspectRatio && (
              <AspectRatioBadge
                data-tooltip-id={TOOLTIP_ID}
                data-tooltip-content={`Changes apply to the ${aspectRatio} version of this template`}
              >
                {aspectRatio}
              </AspectRatioBadge>
            )}
          </TargetRow>
        )}
        <OptionsList>
          {options.map(option => (
            <OptionGroup key={option.type}>
              <OptionRow
                data-tooltip-id={TOOLTIP_ID}
                data-tooltip-content={
                  option.exists
                    ? undefined
                    : `This template has no ${option.label.toLowerCase()} yet. Add one from the panel first.`
                }
              >
                <Switch
                  label={`Include ${option.label}`}
                  labelColor={
                    option.exists ? 'var(--foreground)' : 'var(--text-muted)'
                  }
                  checked={option.exists && enabledStates[option.type]}
                  onClick={() => handleToggleEnabled(option.type)}
                  disabled={!option.exists}
                  checkedColor="var(--accent)"
                  labelSize="14px"
                  small
                />
              </OptionRow>
              {canEditLockPosition && option.exists && (
                <PlacementRow>
                  <Switch
                    label={`Lock ${option.label} Position`}
                    labelColor="var(--foreground)"
                    checked={lockSelections[option.type] ?? false}
                    onClick={() => handleToggleLock(option.type)}
                    checkedColor="var(--accent)"
                    labelSize="13px"
                    small
                  />
                </PlacementRow>
              )}
              {option.hasPlacement && (
                <PlacementRow
                  data-tooltip-id={TOOLTIP_ID}
                  data-tooltip-content={
                    !option.activeInEditor
                      ? `Turn the ${option.label.toLowerCase()} on in the editor first.`
                      : option.placementDiffersFromTemplate
                        ? `Saves where the ${option.label.toLowerCase()} sits on the canvas as this version's default.`
                        : `The ${option.label.toLowerCase()} is already where this version of the template puts it.`
                  }
                >
                  <Switch
                    label={`Save ${option.label} Position`}
                    labelColor={
                      option.activeInEditor
                        ? 'var(--foreground)'
                        : 'var(--text-muted)'
                    }
                    checked={
                      option.activeInEditor && placementSelections[option.type]
                    }
                    onClick={() => handleTogglePlacement(option.type)}
                    disabled={!option.activeInEditor}
                    checkedColor="var(--accent)"
                    labelSize="13px"
                    small
                  />
                </PlacementRow>
              )}
              {option.hasReplacement && (
                <PlacementRow
                  data-tooltip-id={TOOLTIP_ID}
                  data-tooltip-content={`Makes the ${option.label.toLowerCase()} you swapped in the panel this version's default.`}
                >
                  <Switch
                    label={`Save Replaced ${option.label}`}
                    labelColor="var(--foreground)"
                    checked={replacementSelections[option.type]}
                    onClick={() => handleToggleReplacement(option.type)}
                    checkedColor="var(--accent)"
                    labelSize="13px"
                    small
                  />
                </PlacementRow>
              )}
            </OptionGroup>
          ))}
        </OptionsList>
        <ButtonRow
          data-tooltip-id={TOOLTIP_ID}
          data-tooltip-content={
            onUpdate && !hasChanges()
              ? 'Nothing to update yet. Change a toggle above first.'
              : undefined
          }
        >
          {onUpdate && (
            <UpdateButton
              onClick={handleUpdate}
              disabled={isUpdating || isSavingCopy || !hasChanges()}
            >
              {isUpdating ? 'Updating…' : 'Update Video Template'}
            </UpdateButton>
          )}
        </ButtonRow>
        <SaveAsCopySection>
          {showCopyInput ? (
            <>
              <CopyNameInput
                type="text"
                placeholder="New template name"
                value={copyLabel}
                onChange={e => setCopyLabel(e.target.value)}
                onKeyDown={handleCopyKeyDown}
                autoFocus
              />
              <CopyButtonRow>
                <SaveAsCopyButton
                  onClick={handleSaveAsCopy}
                  disabled={isSavingCopy || !copyLabel.trim()}
                >
                  {isSavingCopy ? 'Saving…' : 'Save Copy'}
                </SaveAsCopyButton>
                <CancelCopyButton
                  onClick={() => {
                    setShowCopyInput(false);
                    setCopyLabel('');
                  }}
                  disabled={isSavingCopy}
                >
                  Cancel
                </CancelCopyButton>
              </CopyButtonRow>
            </>
          ) : (
            <SaveAsCopyButton
              onClick={() => setShowCopyInput(true)}
              disabled={isUpdating}
            >
              Save as New Template
            </SaveAsCopyButton>
          )}
        </SaveAsCopySection>
        {/* Rendered unconditionally — react-tooltip is inert without a
            matching data-tooltip-content, so rows without a hint stay quiet. */}
        <Tooltip
          id={TOOLTIP_ID}
          place="top"
          style={{ ...MODAL_TOOLTIP_STYLE, maxWidth: '280px' }}
        />
      </ModalWrapper>
    </Modal>
  );
}

const ModalWrapper = styled.div`
  background-color: var(--background);
  color: var(--foreground);
  width: 400px;
  box-shadow: 8px 8px 16px 0px rgba(0, 0, 0, 0.4);
  border-radius: 10px;
  padding: 24px;
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2`
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`;

const TargetRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  min-width: 0;
  /* Pull up under the title so the two read as one heading block. */
  margin-top: -12px;
`;

const TemplateName = styled.span`
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AspectRatioBadge = styled.span`
  ${dimensionBadgeBase}
  display: inline-flex;
  flex-shrink: 0;
  border-color: var(--border);
  color: var(--foreground);
  font-family: var(--font-primary);
  font-weight: var(--font-weight-medium);
`;

const CloseButton = styled.button`
  color: var(--text-muted);
  background: transparent;
  border: none;
  padding: var(--spacing-sm);
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OptionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
`;

const PlacementRow = styled.div`
  display: flex;
  align-items: center;
  padding-left: 20px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 4px;
`;

const UpdateButton = styled.button`
  background-color: var(--primary);
  color: var(--background);
  border: none;
  border-radius: 8px;
  padding: 10px 32px;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    background-color: var(--surface);
    color: var(--text-muted);
    border: 1px solid var(--border);
    cursor: not-allowed;
  }
`;

const SaveAsCopySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid var(--border);
  padding-top: 16px;
`;

const SaveAsCopyButton = styled.button`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 32px;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
  width: 100%;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CopyNameInput = styled.input`
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 12px;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  color: var(--foreground);
  outline: none;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: var(--accent);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const CopyButtonRow = styled.div`
  display: flex;
  gap: 8px;
`;

const CancelCopyButton = styled.button`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 16px;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
